import {
  Eventcalendar,
  type MbscCalendarEvent,
  type MbscEventcalendarView,
  toast,
} from '@mobiscroll/react';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import { PageHeader } from '@/shared/components/PageHeader';
import { showInfoToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Calendar, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  useCreatePlannerEvent,
  useDeletePlannerEvent,
  usePlannerEvents,
  usePlannerResources,
  useUpdatePlannerEvent,
  useValidatePlannerEvent,
} from '../hooks/usePlanner';

export default function PlannerPage() {
  const [view, setView] = useState<'day' | 'week' | 'month'>('week');
  const [selectedTeamId, setSelectedTeamId] = useState<number | undefined>(undefined);

  // Date range for current view — default to current week
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + (6 - d.getDay()));
    d.setHours(23, 59, 59, 999);
    return d;
  });

  const startIso = rangeStart.toISOString();
  const endIso = rangeEnd.toISOString();

  const { data: resources = [], isLoading: resourcesLoading } = usePlannerResources(selectedTeamId);
  const { data: serverEvents = [], isLoading: eventsLoading } = usePlannerEvents(
    startIso,
    endIso,
    selectedTeamId,
  );

  const createEvent = useCreatePlannerEvent();
  const updateEvent = useUpdatePlannerEvent();
  const deleteEvent = useDeletePlannerEvent();
  const validateEvent = useValidatePlannerEvent();

  const isLoading = resourcesLoading || eventsLoading;

  const calendarView = useMemo<MbscEventcalendarView>(
    () => ({
      timeline: {
        type: view,
        startDay: 0,
        endDay: 6,
        startTime: '07:00',
        endTime: '19:00',
        timeCellStep: 60,
        timeLabelStep: 60,
      },
    }),
    [view],
  );

  // Map server events to Mobiscroll format
  const mbscEvents = useMemo<MbscCalendarEvent[]>(
    () =>
      serverEvents.map((e) => ({
        id: e.id,
        title: e.title,
        start: new Date(e.start),
        end: new Date(e.end),
        resource: e.userId,
        color: e.color ?? undefined,
        editable: true,
      })),
    [serverEvents],
  );

  // Map resources to Mobiscroll format
  const mbscResources = useMemo(
    () =>
      resources.map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
      })),
    [resources],
  );

  const handleEventCreate = useCallback(
    async (args: any) => {
      const userId = Number(args.event?.resource ?? args.resource);
      const start = args.event?.start ?? args.start;
      const end = args.event?.end ?? args.end;

      if (!userId || !start || !end) return false;

      // Conflict check
      const validation = await validateEvent.mutateAsync({
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        userId,
      });

      if (validation.hasConflict) {
        toast({
          message: `Scheduling conflict: ${validation.conflicts.length} overlapping event(s) for this person.`,
          color: 'danger',
        });
        return false;
      }

      try {
        await createEvent.mutateAsync({
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          userId,
          teamId: selectedTeamId,
        });
      } catch {
        toast({ message: 'Failed to create event. Please try again.', color: 'danger' });
        return false;
      }

      return true;
    },
    [createEvent, validateEvent, selectedTeamId],
  );

  const handleEventUpdate = useCallback(
    async (args: any) => {
      const eventId = Number(args.event?.id);
      const start = args.event?.start;
      const end = args.event?.end;
      const userId = args.event?.resource != null ? Number(args.event.resource) : undefined;

      if (!eventId || !start || !end) return false;

      // Conflict check (excluding the event being moved)
      if (userId) {
        const validation = await validateEvent.mutateAsync({
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          userId,
          excludeId: eventId,
        });

        if (validation.hasConflict) {
          toast({
            message: `Scheduling conflict: ${validation.conflicts.length} overlapping event(s).`,
            color: 'danger',
          });
          return false;
        }
      }

      await updateEvent.mutateAsync({
        id: eventId,
        data: {
          start: new Date(start).toISOString(),
          end: new Date(end).toISOString(),
          ...(userId && { userId }),
        },
      });

      return true;
    },
    [updateEvent, validateEvent],
  );

  const handleEventDelete = useCallback(
    async (args: any) => {
      const eventId = Number(args.event?.id);
      if (!eventId) return false;
      await deleteEvent.mutateAsync(eventId);
      return true;
    },
    [deleteEvent],
  );

  // Expand date range when user navigates
  const handlePageChange = useCallback((args: any) => {
    if (args.firstDay && args.lastDay) {
      const start = new Date(args.firstDay);
      start.setHours(0, 0, 0, 0);
      const end = new Date(args.lastDay);
      end.setHours(23, 59, 59, 999);
      setRangeStart(start);
      setRangeEnd(end);
    }
  }, []);

  // Build team options from resources (unique teams)
  const teamOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of resources) {
      if (r.teamId && r.teamName && !seen.has(r.teamId)) {
        seen.set(r.teamId, r.teamName);
      }
    }
    return Array.from(seen.entries());
  }, [resources]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Schedule"
        subtitle="Schedule and manage jobs across teams"
        icon={<Calendar className="w-7 h-7" style={{ color: '#E84A2B' }} />}
        actions={
          <div className="flex items-center gap-2">
            {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}

            <Select
              value={selectedTeamId ? String(selectedTeamId) : 'all'}
              onValueChange={(v) => {
                const nextId = v === 'all' ? undefined : Number(v);
                setSelectedTeamId(nextId);
                if (nextId === undefined) {
                  showInfoToast('Showing all teams');
                } else {
                  const teamName = teamOptions.find(([id]) => id === nextId)?.[1];
                  showInfoToast(teamName ? `Showing team: ${teamName}` : 'Filter applied');
                }
              }}
            >
              <SelectTrigger className="w-44 border-border rounded-lg">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teamOptions.map(([id, name]) => (
                  <SelectItem key={id} value={String(id)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
              {(['day', 'week', 'month'] as const).map((v) => (
                <Button
                  key={v}
                  variant={view === v ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView(v)}
                  className={view === v ? 'bg-[#E84A2B] hover:bg-[#E84A2B]/90' : ''}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-hidden px-6 pb-6">
        <div className="h-full bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <style>{`
            .mbsc-timeline-event {
              border-radius: 6px;
              border-left-width: 4px;
              font-size: 13px;
              padding: 6px 8px;
              font-weight: 500;
            }
            .mbsc-timeline-resource {
              font-weight: 600;
              color: #18181b;
              background: #f9fafb;
              border-right: 2px solid #e5e7eb;
              padding: 12px 16px;
            }
            .mbsc-timeline-row {
              border-bottom: 1px solid #e5e7eb;
              min-height: 60px;
            }
            .mbsc-timeline-header-date {
              font-weight: 600;
              color: #18181b;
              background: #f4f4f5;
            }
            .mbsc-timeline-column {
              background: #ffffff;
              border-right: 1px solid #f0f0f0;
            }
            .mbsc-timeline-header {
              background: #f4f4f5;
              font-weight: 600;
              border-bottom: 2px solid #e5e7eb;
            }
            .mbsc-timeline-day {
              border-right: 1px solid #e5e7eb;
            }
          `}</style>

          <Eventcalendar
            view={calendarView}
            data={mbscEvents}
            resources={mbscResources}
            clickToCreate={true}
            dragToCreate={true}
            dragToMove={true}
            dragToResize={true}
            onEventCreate={handleEventCreate}
            onEventUpdate={handleEventUpdate}
            onEventDelete={handleEventDelete}
            onPageChange={handlePageChange}
            theme="material"
            themeVariant="light"
            cssClass="planner-calendar"
          />
        </div>
      </div>
    </div>
  );
}
